import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, errorMessage } from '../../lib/api.js';
import {
  useBlogs, useQuota, useTemplates, useInvalidate, useMutation, type Blog,
} from '../../lib/queries.js';
import {
  Badge, Button, Card, EmptyState, ErrorBox, Input, Loading, Modal, PageHeader,
  STATUS_TONE, useToast, cx,
  IconBlog,
  IconEye, IconPlus,
} from '../../components/ui/index.js';
import { STATUS_LABEL, date } from '../../lib/format.js';
import { hasRole, useRole } from '../../lib/auth.js';
import { PagePreviewModal } from '../../components/PagePreviewModal.js';

/** slug ที่เดาให้จากชื่อ — ผู้ใช้แก้เองได้ แต่ไม่ต้องคิดเองตั้งแต่ต้น */
function suggestSlug(name: string): string {
  return name
    .normalize('NFC').trim().toLowerCase()
    .replace(/[^\p{L}\p{N}\p{M}]+/gu, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 63);
}

function CreateBlogModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const toast = useToast();
  const invalidate = useInvalidate();
  const themes = useTemplates('BLOG_THEME');
  const [form, setForm] = useState({ title: '', slug: '', slugTouched: false, themeCode: 'magazine' });
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => api.post<{ data: Blog }>('/v1/blogs', {
      title: form.title, slug: form.slug, themeCode: form.themeCode, defaultLocale: 'th',
    }),
    onSuccess: () => {
      invalidate(['blogs'], ['quota']);
      toast('สร้างบล็อกเรียบร้อย');
      onClose();
      setForm({ title: '', slug: '', slugTouched: false, themeCode: 'magazine' });
    },
    onError: (err) => setError(errorMessage(err)),
  });

  return (
    <Modal
      open={open} onClose={onClose} title="สร้างบล็อกใหม่"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>ยกเลิก</Button>
          <Button loading={create.isPending} disabled={!form.title || !form.slug}
                  onClick={() => { setError(null); create.mutate(); }}>สร้างบล็อก</Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div>}
        <Input
          label="ชื่อบล็อก" required autoFocus value={form.title}
          onChange={(e) => setForm((f) => ({
            ...f, title: e.target.value,
            slug: f.slugTouched ? f.slug : suggestSlug(e.target.value),
          }))}
        />
        <Input
          label="URL ของบล็อก" required value={form.slug}
          onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value, slugTouched: true }))}
          hint={`บล็อกจะอยู่ที่ /b/${form.slug || '{url}'} — เปลี่ยนภายหลังได้แต่ลิงก์เดิมจะถูก redirect`}
        />
        <div>
          <span className="label">ธีม</span>
          <div className="grid gap-2 sm:grid-cols-2">
            {themes.data?.filter((t) => t.available).map((t) => (
              <button
                key={t.code} type="button"
                onClick={() => setForm((f) => ({ ...f, themeCode: t.code }))}
                className={cx(
                  'rounded-lg border p-3 text-left transition-colors',
                  form.themeCode === t.code
                    ? 'border-[var(--brand)] bg-[var(--brand-soft)]'
                    : 'border-[var(--line)] hover:border-[var(--line)]',
                )}
              >
                <span className="block text-sm font-semibold text-[var(--ink)]">{t.name}</span>
                <span className="mt-0.5 block text-xs text-[var(--ink-3)]">{t.description}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </Modal>
  );
}

export function BlogListPage() {
  const blogs = useBlogs();
  const quota = useQuota();
  const role = useRole();
  const [creating, setCreating] = useState(false);
  const [previewing, setPreviewing] = useState<Blog | null>(null);

  const canCreate = hasRole(role, 'ADMIN');
  const atLimit = quota.data ? quota.data.blogs.used >= quota.data.blogs.max : false;

  if (blogs.isLoading) return <Loading />;
  if (blogs.error) return <ErrorBox error={blogs.error} onRetry={() => void blogs.refetch()} />;

  return (
    <>
      <PageHeader
        title="บล็อก"
        description={quota.data
          ? `ใช้ไป ${quota.data.blogs.used} จาก ${quota.data.blogs.max} บล็อกในแพ็กเกจปัจจุบัน`
          : undefined}
        actions={canCreate && (
          <Button onClick={() => setCreating(true)} disabled={atLimit}
                  title={atLimit ? 'ถึงเพดานของแพ็กเกจแล้ว — อัปเกรดเพื่อสร้างเพิ่ม' : undefined}>
            + สร้างบล็อก
          </Button>
        )}
      />

      {!blogs.data?.length ? (
        <EmptyState
          icon={<IconBlog size={22} />} title="ยังไม่มีบล็อก"
          description="สร้างบล็อกแรกเพื่อเริ่มเขียนบทความและสร้าง widget ไปติดบนเว็บบริษัท"
          action={canCreate && <Button onClick={() => setCreating(true)}>สร้างบล็อกแรก</Button>}
        />
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {blogs.data.map((blog) => (
            <Card key={blog.id} className="flex flex-col p-5 transition-shadow hover:shadow-md">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <Link to={`/blogs/${blog.id}`}
                        className="block truncate text-base font-semibold text-[var(--ink)] hover:text-[var(--brand)]">
                    {blog.title}
                  </Link>
                  <p className="mt-0.5 truncate font-mono text-xs text-[var(--ink-3)]">/b/{blog.slug}</p>
                </div>
                <Badge tone={STATUS_TONE[blog.status] ?? 'gray'}>{STATUS_LABEL[blog.status] ?? blog.status}</Badge>
              </div>

              {blog.description && (
                <p className="mt-2.5 line-clamp-2 text-sm text-[var(--ink-3)]">{blog.description}</p>
              )}

              <dl className="mt-4 grid grid-cols-3 gap-2 text-center">
                {[
                  ['เผยแพร่', blog.stats?.published ?? 0],
                  ['ฉบับร่าง', blog.stats?.draft ?? 0],
                  ['ทั้งหมด', blog.stats?.total ?? 0],
                ].map(([label, value]) => (
                  <div key={label as string} className="rounded-lg bg-[var(--sunken)] py-2">
                    <dd className="text-lg font-bold tabular-nums text-[var(--ink)]">{value as number}</dd>
                    <dt className="text-[11px] text-[var(--ink-3)]">{label as string}</dt>
                  </div>
                ))}
              </dl>

              <div className="mt-4 flex items-center justify-between gap-2 border-t border-[var(--line-soft)] pt-3">
                <span className="text-xs text-[var(--ink-3)]">สร้าง {date(blog.createdAt)}</span>
                <div className="flex gap-1.5">
                  <Button size="sm" variant="ghost" icon={<IconEye size={14} />}
                          onClick={() => setPreviewing(blog)}>ดูตัวอย่าง</Button>
                  <Link to={`/blogs/${blog.id}`}><Button size="sm" variant="secondary">จัดการ</Button></Link>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <CreateBlogModal open={creating} onClose={() => setCreating(false)} />
      <PagePreviewModal
        open={Boolean(previewing)}
        onClose={() => setPreviewing(null)}
        title={previewing ? `ตัวอย่าง — ${previewing.title}` : ''}
        url={previewing?.url ?? null}
        height={600}
        /* บล็อกที่ยังไม่เผยแพร่ไม่มีหน้าสาธารณะ — บอกให้ชัดแทนที่จะโชว์ 404 ในกล่อง */
        unavailable={previewing && previewing.status !== 'PUBLISHED'
          ? 'บล็อกนี้ยังไม่ได้เผยแพร่ จึงยังไม่มีหน้าสาธารณะให้ดู — เปลี่ยนสถานะเป็น "เผยแพร่" ในหน้าจัดการก่อน'
          : undefined}
        meta={previewing && <span className="font-mono text-[11px] text-[var(--ink-3)]">{previewing.url}</span>}
        footerExtra={previewing && (
          <Link to={`/blogs/${previewing.id}`}><Button variant="secondary">จัดการ</Button></Link>
        )}
      />
    </>
  );
}
